/**
 * Word document templates for lesson export
 */

import {
  Paragraph,
  TextRun,
  HeadingLevel,
  ImageRun
} from 'docx';
import { Lesson, TimelineStep } from '../types';
import { htmlToText } from '../utils/htmlToPowerPoint';
import { createResourceVisualization, getResourceTypeName } from '../utils/resourceVisualizer';
import { downloadImageAsBuffer } from '../utils/imageDownloader';
import { DEFAULT_FONT_CONFIG, DEFAULT_FONT_NAME } from './wordStyles';

/**
 * Converts CSS color values to Word hex format (RRGGBB)
 * Handles: inherit, transparent, named colors, hex (#RRGGBB), rgb(), rgba()
 * Returns default black (000000) for invalid/inherit values
 */
function convertColorToWordHex(color: string | undefined | null): string {
  if (!color || typeof color !== 'string') {
    return '000000'; // Default black
  }
  
  const normalizedColor = color.trim().toLowerCase();
  
  // Handle CSS keywords
  if (normalizedColor === 'inherit' || normalizedColor === 'initial' || normalizedColor === 'unset' || normalizedColor === 'transparent') {
    return '000000'; // Default black
  }
  
  // Handle hex colors (#RRGGBB or RRGGBB)
  if (normalizedColor.startsWith('#')) {
    const hex = normalizedColor.substring(1);
    if (/^[0-9a-f]{6}$/i.test(hex)) {
      return hex.toUpperCase();
    }
    // Handle short hex (#RGB -> RRGGBB)
    if (/^[0-9a-f]{3}$/i.test(hex)) {
      return (hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]).toUpperCase();
    }
  } else if (/^[0-9a-f]{6}$/i.test(normalizedColor)) {
    // Already in hex format without #
    return normalizedColor.toUpperCase();
  }
  
  // Handle rgb() and rgba()
  const rgbMatch = normalizedColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
    const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
    const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
    return (r + g + b).toUpperCase();
  }
  
  // Handle named colors (basic set)
  const namedColors: Record<string, string> = {
    'black': '000000',
    'white': 'FFFFFF',
    'red': 'FF0000',
    'green': '008000',
    'blue': '0000FF',
    'yellow': 'FFFF00',
    'cyan': '00FFFF',
    'magenta': 'FF00FF',
    'gray': '808080',
    'grey': '808080',
    'orange': 'FFA500',
    'purple': '800080',
    'pink': 'FFC0CB',
    'brown': 'A52A2A',
  };
  
  if (namedColors[normalizedColor]) {
    return namedColors[normalizedColor];
  }
  
  // Default to black for unknown colors
  return '000000';
}

// Removed convertHtmlToWordParagraphs - now using same approach as PowerPoint:
// htmlToText() converts HTML to plain text, then createParagraphs() handles line breaks
// This ensures consistency between Word and PowerPoint exports

export interface WordExportOptions {
  includeResources?: boolean;
  includeAssessments?: boolean;
  includeNotes?: boolean;
  detailLevel?: 'summary' | 'complete';
}

/**
 * Helper function to sanitize text for Word
 * More aggressive cleaning to prevent metadata insertion
 */
function sanitizeText(text: string | null | undefined): string {
  if (!text) return '';
  
  let cleaned = String(text);

  // Remove Word metadata patterns that can leak into content
  // Pattern: "Normal\n0\n21\nfalse\nfalse\nfalse\nFR\nX-NONE\nX-NONE"
  cleaned = cleaned.replace(/Normal\s+\d+\s+\d+\s+false\s+false\s+false\s+[A-Z]{2}\s+X-NONE\s+X-NONE/gi, '');
  
  // Remove individual metadata lines that might appear separately
  cleaned = cleaned.replace(/^\s*Normal\s*$/gmi, ''); // Remove lines that are just "Normal"
  cleaned = cleaned.replace(/^\s*\d+\s*$/gm, ''); // Remove lines that are just numbers (likely metadata)
  cleaned = cleaned.replace(/^\s*false\s*$/gmi, ''); // Remove lines that are just "false"
  cleaned = cleaned.replace(/^\s*true\s*$/gmi, ''); // Remove lines that are just "true"
  cleaned = cleaned.replace(/^\s*FR\s*$/gmi, ''); // Remove lines that are just "FR" (language code)
  cleaned = cleaned.replace(/^\s*EN\s*$/gmi, ''); // Remove lines that are just "EN" (language code)
  cleaned = cleaned.replace(/^\s*X-NONE\s*$/gmi, ''); // Remove lines that are just "X-NONE"

  // Normalize line breaks
  cleaned = cleaned.replace(/\r\n/g, '\n');
  cleaned = cleaned.replace(/\r/g, '\n');
  
  // Remove empty lines (that might have been created by removing metadata)
  cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n');
  
  // Replace multiple consecutive newlines with maximum of 2
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  
  return cleaned;
}

/**
 * Creates a heading paragraph
 */
function createHeading(text: string, level: typeof HeadingLevel[keyof typeof HeadingLevel] = HeadingLevel.HEADING_1): Paragraph {
  const sanitized = sanitizeText(text);
  if (!sanitized || sanitized.length === 0) {
    // Return a paragraph with a space instead of empty text to avoid metadata issues
  return new Paragraph({
    children: [
      new TextRun({
        text: ' ',
        font: DEFAULT_FONT_NAME
      })
    ],
    heading: level,
    spacing: { after: 240, before: 160 } // Adjusted to match UI spacing
  });
  }
  return new Paragraph({
    children: [
      new TextRun({
        text: sanitized,
        font: DEFAULT_FONT_NAME,
        bold: true, // Headings are typically bold (font-bold)
        // Match UI: text-xl sm:text-2xl = 20-24pt, use 24pt for Heading 1, 20pt for Heading 2
        size: level === HeadingLevel.HEADING_1 ? 48 : level === HeadingLevel.HEADING_2 ? 40 : DEFAULT_FONT_CONFIG.size
      })
    ],
    heading: level,
    spacing: { after: 240, before: 160 } // Adjusted to match UI spacing (mb-2 = 8px = ~160 twips)
  });
}

/**
 * Creates one or more paragraphs from text, handling line breaks
 * Returns an array of paragraphs (one per line) to preserve line breaks
 */
function createParagraphs(text: string, options?: {
  bold?: boolean;
  italic?: boolean;
  size?: number;
  fontSize?: number; // Alias for size for compatibility
  color?: string; // Hex color code (e.g., '1E3A8A' for blue-900)
  spacing?: { before?: number; after?: number };
}): Paragraph[] {
  // Use fontSize if provided, otherwise use size
  const finalSize = options?.fontSize || options?.size;
  const sanitized = sanitizeText(text);
  // Ensure we never create a paragraph with empty text
  const finalText = sanitized && sanitized.length > 0 ? sanitized : ' ';
  
  // Split text by line breaks - each line becomes a separate paragraph
  const lines = finalText.split(/\r?\n/);
  const paragraphs: Paragraph[] = [];
  
  lines.forEach((line, index) => {
    // Skip empty lines except if it's the only line
    if (line.trim().length === 0 && lines.length > 1) {
      return;
    }
    
    const lineText = line.trim().length > 0 ? line : ' ';
    
    paragraphs.push(
      new Paragraph({
        children: [
            new TextRun({
              text: lineText,
              font: DEFAULT_FONT_NAME,
              bold: options?.bold,
              italics: options?.italic,
              size: finalSize ? finalSize * 2 : DEFAULT_FONT_CONFIG.size, // docx uses half-points
              color: options?.color || DEFAULT_FONT_CONFIG.color // Use provided color or default black
            })
        ],
        spacing: {
          before: index === 0 ? (options?.spacing?.before || 0) : 0,
          after: index === lines.length - 1 ? (options?.spacing?.after || 200) : 0
        }
      })
    );
  });
  
  // If no paragraphs were created (all lines were empty), create one with space
  if (paragraphs.length === 0) {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: ' ',
            font: DEFAULT_FONT_NAME,
            bold: options?.bold,
            italics: options?.italic,
            size: finalSize ? finalSize * 2 : DEFAULT_FONT_CONFIG.size,
            color: options?.color || DEFAULT_FONT_CONFIG.color
          })
        ],
        spacing: {
          before: options?.spacing?.before || 0,
          after: options?.spacing?.after || 200
        }
      })
    );
  }
  
  return paragraphs;
}

/**
 * Creates a regular paragraph (single paragraph, line breaks ignored)
 * For line break support, use createParagraphs instead
 */
function createParagraph(text: string, options?: {
  bold?: boolean;
  italic?: boolean;
  size?: number;
  color?: string; // Hex color code (e.g., '1E3A8A' for blue-900)
  spacing?: { before?: number; after?: number };
}): Paragraph {
  const paragraphs = createParagraphs(text, options);
  // Return first paragraph for backward compatibility
  return paragraphs[0];
}

/**
 * Creates bullet list items with support for line breaks
 * Returns an array of paragraphs (one per line) to preserve line breaks
 */
function createBulletItems(text: string, color?: string): Paragraph[] {
  const sanitized = sanitizeText(text);
  // Ensure we never create a bullet item with empty text
  const finalText = sanitized && sanitized.length > 0 ? sanitized : ' ';
  
  // Split text by line breaks - each line becomes a separate bullet item
  const lines = finalText.split('\n');
  const paragraphs: Paragraph[] = [];
  
  lines.forEach((line, index) => {
    // Skip empty lines except if it's the only line
    if (line.trim().length === 0 && lines.length > 1) {
      return;
    }
    
    const lineText = line.trim().length > 0 ? line : ' ';
    // Add bullet only to the first line
    const bulletText = index === 0 ? `• ${lineText}` : `  ${lineText}`; // Indent subsequent lines
    
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: bulletText,
            font: DEFAULT_FONT_NAME,
            size: DEFAULT_FONT_CONFIG.size,
            color: color || DEFAULT_FONT_CONFIG.color // Use provided color or default black
          })
        ],
        spacing: { after: index === lines.length - 1 ? 100 : 0 }
      })
    );
  });
  
  // If no paragraphs were created (all lines were empty), create one with bullet
  if (paragraphs.length === 0) {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: '• ',
            font: DEFAULT_FONT_NAME,
            size: DEFAULT_FONT_CONFIG.size,
            color: color || DEFAULT_FONT_CONFIG.color
          })
        ],
        spacing: { after: 100 }
      })
    );
  }
  
  return paragraphs;
}

/**
 * Creates a bullet list item (single paragraph, line breaks ignored)
 * For line break support, use createBulletItems instead
 */
function createBulletItem(text: string, color?: string): Paragraph {
  const paragraphs = createBulletItems(text, color);
  // Return first paragraph for backward compatibility
  return paragraphs[0];
}

/**
 * Creates the title section
 */
export function createTitleSection(lesson: Lesson): Paragraph[] {
  const sections: Paragraph[] = [];
  
  // Title - styled to match UI (text-xl sm:text-2xl font-bold)
  sections.push(createHeading(lesson.metadata.title || 'Untitled Lesson', HeadingLevel.HEADING_1));
  
  // Subtitle - styled to match UI (text-sm sm:text-base text-gray-600)
  const subtitle = `${lesson.metadata.subject || ''} - Grade ${lesson.metadata.grade || ''}`;
  if (subtitle.trim()) {
    sections.push(createParagraph(subtitle, { 
      size: 14, // text-sm equivalent
      spacing: { after: 160 } // mb-2 equivalent (8px = ~160 twips)
    }));
  }
  
  // Metadata - styled to match UI (text-sm text-gray-500)
  const metadata: string[] = [];
  if (lesson.metadata.duration_minutes) {
    metadata.push(`Durée: ${lesson.metadata.duration_minutes} minutes`);
  }
  if (lesson.metadata.language) {
    metadata.push(`Langue: ${lesson.metadata.language}`);
  }
  if (lesson.metadata.version) {
    metadata.push(`Version: ${lesson.metadata.version}`);
  }
  
  if (metadata.length > 0) {
    sections.push(createParagraph(metadata.join(' • '), {
      size: 12, // text-xs equivalent
      spacing: { after: 400 } // mb-4 equivalent
    }));
  }
  
  return sections;
}

/**
 * Creates the objectives section
 */
export function createObjectivesSection(lesson: Lesson): Paragraph[] {
  const sections: Paragraph[] = [];
  
  if (!lesson.objectives || lesson.objectives.length === 0) {
    return sections;
  }
  
  sections.push(createHeading('Objectifs d\'Apprentissage', HeadingLevel.HEADING_1));
  
  lesson.objectives.forEach(objective => {
    if (objective && objective.trim()) {
      sections.push(createBulletItem(objective));
    }
  });
  
  return sections;
}

/**
 * Creates the success criteria section
 */
export function createSuccessCriteriaSection(lesson: Lesson): Paragraph[] {
  const sections: Paragraph[] = [];
  
  if (!lesson.success_criteria || lesson.success_criteria.length === 0) {
    return sections;
  }
  
  sections.push(createHeading('Critères de Réussite', HeadingLevel.HEADING_1));
  
  lesson.success_criteria.forEach(criterion => {
    if (criterion && criterion.trim()) {
      sections.push(createBulletItem(criterion));
    }
  });
  
  return sections;
}

/**
 * Creates the standards and prerequisites section
 */
export function createStandardsSection(lesson: Lesson): Paragraph[] {
  const sections: Paragraph[] = [];
  
  const hasStandards = lesson.standards_alignment && lesson.standards_alignment.length > 0;
  const hasPrerequisites = lesson.prerequisites && lesson.prerequisites.trim();
  
  if (!hasStandards && !hasPrerequisites) {
    return sections;
  }
  
  sections.push(createHeading('Alignement avec les Standards', HeadingLevel.HEADING_1));
  
  if (hasStandards) {
    sections.push(createParagraph('Standards:', { bold: true, spacing: { after: 100 } }));
    lesson.standards_alignment.forEach(standard => {
      if (standard && standard.trim()) {
        sections.push(createBulletItem(standard));
      }
    });
  }
  
  if (hasPrerequisites) {
    sections.push(createParagraph('Prérequis:', { bold: true, spacing: { before: 200, after: 100 } }));
    // Use createParagraphs to preserve line breaks in prerequisites
    sections.push(...createParagraphs(lesson.prerequisites));
  }
  
  return sections;
}

/**
 * Creates the materials section
 */
export function createMaterialsSection(lesson: Lesson): Paragraph[] {
  const sections: Paragraph[] = [];
  
  const hasDigital = lesson.materials.digital && lesson.materials.digital.length > 0;
  const hasPhysical = lesson.materials.physical_alternatives && lesson.materials.physical_alternatives.length > 0;
  
  if (!hasDigital && !hasPhysical) {
    return sections;
  }
  
  sections.push(createHeading('Matériel Nécessaire', HeadingLevel.HEADING_1));
  
  if (hasDigital) {
    sections.push(createParagraph('Matériel Numérique:', { bold: true, spacing: { after: 100 } }));
    lesson.materials.digital.forEach(material => {
      if (material && material.trim()) {
        sections.push(createBulletItem(material));
      }
    });
  }
  
  if (hasPhysical) {
    sections.push(createParagraph('Alternatives Physiques:', { bold: true, spacing: { before: 200, after: 100 } }));
    lesson.materials.physical_alternatives.forEach(material => {
      if (material && material.trim()) {
        sections.push(createBulletItem(material));
      }
    });
  }
  
  return sections;
}

/**
 * Creates timeline step sections with images
 */
// Global counter for unique image IDs to prevent deduplication
let imageCounter = 0;

export async function createTimelineStepSections(
  step: TimelineStep,
  index: number,
  lesson: Lesson,
  options: WordExportOptions,
  imageCache?: Map<string, { buffer: ArrayBuffer; width: number | null; height: number | null } | null>
): Promise<Paragraph[]> {
  const sections: Paragraph[] = [];
  
  const stepTypeNames: Record<string, string> = {
    warmup: 'Échauffement',
    launch: 'Lancement',
    direct_instruction: 'Instruction directe',
    guided_practice: 'Pratique guidée',
    independent_practice: 'Pratique autonome',
    closing: 'Clôture'
  };
  
  // Step header - styled to match UI (text-xl sm:text-2xl font-bold)
  const stepTitle = `${index + 1}. ${step.name || 'Untitled Step'}`;
  sections.push(createHeading(stepTitle, HeadingLevel.HEADING_2));
  
  // Step info - styled to match UI (text-sm sm:text-base text-gray-600)
  const stepInfo = [
    `Type: ${stepTypeNames[step.step_type] || step.step_type}`,
    `Durée: ${step.minutes || 0} minutes`
  ].join(' • ');
  
  sections.push(createParagraph(stepInfo, {
    size: 14, // text-sm equivalent (14pt)
    spacing: { after: 320 } // mb-4 equivalent (16px = ~320 twips)
  }));
  
  // Teacher script - styled to match UI (blue background, blue text)
  // UI: bg-blue-50, text-blue-900 (title), text-blue-800 (content)
  if (step.teacher_script && step.teacher_script.length > 0 && options.detailLevel !== 'summary') {
    sections.push(createParagraph('Script de l\'Enseignant', { 
      bold: true, 
      size: 18, // font-semibold equivalent
      color: '1E3A8A', // text-blue-900 (RGB: 30, 58, 138)
      spacing: { before: 200, after: 100 }
    }));
    
    step.teacher_script.forEach((script, scriptIndex) => {
      if (script && script.trim()) {
        // Convert HTML to plain text and create a single paragraph per script item
        let text = htmlToText(script);
        
        // Additional aggressive cleaning for script content
        // Remove any metadata-like patterns
        text = text.replace(/Normal\s+\d+\s+\d+\s+false\s+false\s+false\s+[A-Z]{2}\s+X-NONE\s+X-NONE/gi, '');
        text = text.replace(/^\s*\d+\s*$/gm, ''); // Remove lines that are just numbers
        text = text.replace(/^\s*false\s*$/gmi, ''); // Remove lines that are just "false"
        text = text.replace(/^\s*FR\s*$/gmi, ''); // Remove lines that are just "FR"
        text = text.replace(/^\s*X-NONE\s*$/gmi, ''); // Remove lines that are just "X-NONE"
        
        const sanitized = sanitizeText(text);
        if (sanitized && sanitized.trim().length > 0) {
          // Numbered list item matching UI style (numbered circles) with blue text
          // Use createParagraphs to preserve line breaks
          sections.push(...createParagraphs(`${scriptIndex + 1}. ${sanitized}`, {
            color: '1E40AF', // text-blue-800 (RGB: 30, 64, 175)
            spacing: { after: 160 } // space-y-2 equivalent (8px = ~160 twips)
          }));
        }
      }
    });
  }
  
  // Student actions - styled to match UI (green background, green text, bullet points)
  // UI: bg-green-50, text-green-900 (title), text-green-800 (content)
  if (step.student_actions && step.student_actions.length > 0 && options.detailLevel !== 'summary') {
    sections.push(createParagraph('Actions des Élèves', { 
      bold: true, 
      size: 18, // font-semibold equivalent
      color: '14532D', // text-green-900 (RGB: 20, 83, 45)
      spacing: { before: 300, after: 100 }
    }));
    
      step.student_actions.forEach(action => {
        if (action && action.trim()) {
          // Bullet item matching UI style (green bullet points) with green text
          // Use createBulletItems to preserve line breaks
          sections.push(...createBulletItems(action, '166534')); // text-green-800 (RGB: 22, 101, 52)
        }
      });
  }
  
  // Checks for understanding - styled to match UI (yellow background, yellow text)
  // UI: bg-yellow-50, text-yellow-900 (title), text-yellow-800 (content)
  if (step.checks_for_understanding && step.checks_for_understanding.length > 0 && options.detailLevel !== 'summary') {
    sections.push(createParagraph('Vérifications de Compréhension', { 
      bold: true, 
      size: 18, // font-semibold equivalent
      color: '78350F', // text-yellow-900 (RGB: 120, 53, 15)
      spacing: { before: 300, after: 100 }
    }));
    
    step.checks_for_understanding.forEach((check, checkIndex) => {
      const parts: string[] = [];
      parts.push(`${checkIndex + 1}. Type: ${check.type || ''}`);
      if (check.prompt) {
        const promptText = sanitizeText(check.prompt);
        if (promptText) parts.push(`   Question: ${promptText}`);
      }
      if (check.look_for) {
        const lookForText = sanitizeText(check.look_for);
        if (lookForText) parts.push(`   À observer: ${lookForText}`);
      }
      if (check.expected) {
        const expected = Array.isArray(check.expected) 
          ? check.expected.map(e => sanitizeText(String(e))).filter(e => e).join(', ')
          : sanitizeText(String(check.expected));
        if (expected) parts.push(`   Réponse attendue: ${expected}`);
      }
      
      if (parts.length > 0) {
        const combinedText = parts.join('\n');
        if (combinedText.trim()) {
          sections.push(createParagraph(combinedText, {
            color: '854D0E', // text-yellow-800 (RGB: 133, 77, 14)
            spacing: { after: 160 } // space-y-2 equivalent
          }));
        }
      }
    });
  }
  
  // Resources
  if (options.includeResources && step.resources && step.resources.length > 0) {
    sections.push(createParagraph('Ressources pour cette Étape', { bold: true, size: 20, spacing: { before: 300 } }));
    
    for (const stepResource of step.resources) {
      const resource = lesson.resources[stepResource.resourceId];
      if (resource) {
        const visualization = createResourceVisualization(resource, stepResource.resourceId);
        const description = sanitizeText(visualization.description);
        
        // Handle images specially - try to download and insert them
        if (resource.type === 'image' && visualization.imageUrl) {
          const imageUrl = visualization.imageUrl;
          
          // Get image data from cache or download
          let imageData: { buffer: ArrayBuffer; width: number; height: number } | null = null;
          
            if (imageCache && imageCache.has(imageUrl)) {
            const cached = imageCache.get(imageUrl);
            if (cached && cached.buffer) {
              imageData = {
                buffer: cached.buffer,
                width: cached.width || 800, // Use original dimensions from cache
                height: cached.height || 600
              };
            }
          } else {
            // Fallback: download if not in cache
            console.warn(`Image not in cache, downloading: ${imageUrl}`);
            try {
              const buffer = await downloadImageAsBuffer(imageUrl);
              if (buffer) {
                // Get original image dimensions without resizing
                const blob = new Blob([buffer]);
                const url = URL.createObjectURL(blob);
                const img = new Image();
                await new Promise((resolve, reject) => {
                  img.onload = resolve;
                  img.onerror = reject;
                  img.src = url;
                });
                URL.revokeObjectURL(url);
                imageData = { buffer, width: img.width, height: img.height };
              }
            } catch (error) {
              console.warn(`Failed to download image: ${imageUrl}`, error);
            }
          }
          
          if (imageData && imageData.buffer && imageData.buffer.byteLength > 0) {
            try {
              // Convert ArrayBuffer to Uint8Array for docx library
              // IMPORTANT: docx 9.5.1 requires a proper Uint8Array, not a view
              // Create a fresh copy to ensure we have a proper buffer
              // Note: In browser environment, we use Uint8Array (Buffer is Node.js only)
              
              // Ensure we have a proper ArrayBuffer
              // CRITICAL: Create a complete copy of the buffer to avoid sharing issues
              // when multiple images are processed. Each image needs its own independent buffer.
              const sourceBuffer = imageData.buffer;
              
              // Detect image type from buffer signature AFTER resizing
              // The buffer may have been resized and format may have changed (PNG -> JPEG)
              // So we need to detect from the actual buffer we're using
              const tempBytes = new Uint8Array(sourceBuffer);
              let imageType: 'png' | 'jpg' | 'gif' | 'bmp' | undefined = undefined;
              if (tempBytes.length >= 4) {
                // PNG signature: 89 50 4E 47
                if (tempBytes[0] === 0x89 && tempBytes[1] === 0x50 && 
                    tempBytes[2] === 0x4E && tempBytes[3] === 0x47) {
                  imageType = 'png';
                }
                // JPEG signature: FF D8
                else if (tempBytes[0] === 0xFF && tempBytes[1] === 0xD8) {
                  imageType = 'jpg';
                }
                // GIF signature: 47 49 46 38
                else if (tempBytes[0] === 0x47 && tempBytes[1] === 0x49 && 
                         tempBytes[2] === 0x46 && tempBytes[3] === 0x38) {
                  imageType = 'gif';
                }
                // BMP signature: 42 4D
                else if (tempBytes[0] === 0x42 && tempBytes[1] === 0x4D) {
                  imageType = 'bmp';
                }
              }
              
              // If type detection failed, log a warning
              if (!imageType) {
                console.warn(`Could not detect image type from buffer signature. First bytes: ${Array.from(tempBytes.slice(0, 4)).map(b => `0x${b.toString(16).padStart(2, '0')}`).join(' ')}`);
              }
              
              // Create a complete copy of the buffer (not a view or reference)
              // This ensures each image has its own independent data
              let bufferCopy = sourceBuffer.slice(0); // Creates a new ArrayBuffer copy
              
              // CRITICAL: Modify buffer slightly to ensure uniqueness and prevent deduplication
              // Even if images have identical content after resizing, we need unique buffers
              // We'll add a unique identifier in PNG metadata (IEND chunk) or append a unique byte
              // For PNG files, we can safely append data after the IEND chunk
              if (imageType === 'png' || imageType === undefined) {
                // Create a new buffer with extra space for unique identifier
                const uniqueBuffer = new ArrayBuffer(bufferCopy.byteLength + 4);
                const uniqueView = new Uint8Array(uniqueBuffer);
                const originalView = new Uint8Array(bufferCopy);
                
                // Copy original data
                uniqueView.set(originalView, 0);
                
                // Append unique identifier (4 bytes) after the image data
                // This won't corrupt the image as it's after the IEND chunk
                const uniqueId = imageCounter;
                uniqueView[bufferCopy.byteLength] = (uniqueId >> 24) & 0xFF;
                uniqueView[bufferCopy.byteLength + 1] = (uniqueId >> 16) & 0xFF;
                uniqueView[bufferCopy.byteLength + 2] = (uniqueId >> 8) & 0xFF;
                uniqueView[bufferCopy.byteLength + 3] = uniqueId & 0xFF;
                
                bufferCopy = uniqueBuffer;
              }
              
              const imageBytes = new Uint8Array(bufferCopy); // Create Uint8Array from the copy
              
              console.log(`Processing image ${imageCounter}: original buffer size=${sourceBuffer.byteLength} bytes, unique buffer size=${bufferCopy.byteLength} bytes, Uint8Array length=${imageBytes.length}, width=${imageData.width}, height=${imageData.height}`);
              
              if (imageBytes.length === 0) {
                throw new Error('Image data is empty');
              }
              
              // Verify buffer integrity - check first few bytes
              if (imageBytes.length < 4) {
                throw new Error(`Image buffer too small: ${imageBytes.length} bytes`);
              }
              
              const imageFirstBytes = Array.from(imageBytes.slice(0, 4)).map(b => `0x${b.toString(16).padStart(2, '0')}`).join(' ');
              console.log(`Image first bytes: ${imageFirstBytes}`);
              
              // Ensure we have a valid image signature
              if (imageBytes.length < 4) {
                throw new Error('Image data too small to be valid');
              }
              
              // Get original image dimensions
              const originalWidth = imageData.width || 800;
              const originalHeight = imageData.height || 600;
              
              // Calculate display dimensions to fit A4 page
              // A4 page: 21cm x 29.7cm
              // Usable width with margins (~2.5cm each side): ~16cm = ~604 pixels at 96 DPI
              // Usable height (leaving space for text): ~20cm = ~756 pixels at 96 DPI
              const MAX_WIDTH_PX = 604;  // ~16cm at 96 DPI (A4 usable width)
              const MAX_HEIGHT_PX = 756; // ~20cm at 96 DPI (A4 usable height)
              
              // Calculate aspect ratio
              const aspectRatio = originalHeight / originalWidth;
              
              // Calculate display dimensions while preserving aspect ratio
              let displayWidthPixels: number;
              let displayHeightPixels: number;
              
              if (originalWidth <= MAX_WIDTH_PX && originalHeight <= MAX_HEIGHT_PX) {
                // Image fits within A4 limits, use original dimensions
                displayWidthPixels = originalWidth;
                displayHeightPixels = originalHeight;
              } else if (originalWidth > originalHeight) {
                // Landscape: fit to width
                displayWidthPixels = Math.min(originalWidth, MAX_WIDTH_PX);
                displayHeightPixels = Math.round(displayWidthPixels * aspectRatio);
                
                // If height still exceeds limit, fit to height instead
                if (displayHeightPixels > MAX_HEIGHT_PX) {
                  displayHeightPixels = MAX_HEIGHT_PX;
                  displayWidthPixels = Math.round(displayHeightPixels / aspectRatio);
                }
              } else {
                // Portrait: fit to height
                displayHeightPixels = Math.min(originalHeight, MAX_HEIGHT_PX);
                displayWidthPixels = Math.round(displayHeightPixels / aspectRatio);
                
                // If width still exceeds limit, fit to width instead
                if (displayWidthPixels > MAX_WIDTH_PX) {
                  displayWidthPixels = MAX_WIDTH_PX;
                  displayHeightPixels = Math.round(displayWidthPixels * aspectRatio);
                }
              }
              
              // Ensure minimum size (at least 50px to be visible)
              const MIN_PIXELS = 50;
              if (displayWidthPixels < MIN_PIXELS) {
                displayWidthPixels = MIN_PIXELS;
                displayHeightPixels = Math.round(MIN_PIXELS * aspectRatio);
              }
              if (displayHeightPixels < MIN_PIXELS) {
                displayHeightPixels = MIN_PIXELS;
                displayWidthPixels = Math.round(MIN_PIXELS / aspectRatio);
              }
              
              // Convert pixels to EMUs for Word document
              // Word uses EMU (English Metric Units) as internal unit
              // 1 inch = 914,400 EMU
              // At 96 DPI (standard screen resolution): 1 pixel = 914,400 / 96 = 9,525 EMU
              // Using pixels directly as per user's preference
              const EMU_PER_PIXEL = 1; // Use pixels directly
              const widthEMU = Math.round(displayWidthPixels * EMU_PER_PIXEL);
              const heightEMU = Math.round(displayHeightPixels * EMU_PER_PIXEL);
              
              console.log(`Image dimensions: ${originalWidth}x${originalHeight}px -> display: ${displayWidthPixels}x${displayHeightPixels}px (A4 adapted)`);
              
              // Verify dimensions are valid (not zero or negative)
              // Using original image dimensions, no size limits applied
              if (widthEMU <= 0 || heightEMU <= 0) {
                throw new Error(`Invalid image dimensions: ${widthEMU}x${heightEMU}px`);
              }
              
              // Image type was already detected above before buffer modification
              // If detection failed, try to infer from URL
              
              console.log(`Inserting image: ${imageUrl.substring(0, 50)}..., size: ${(imageBytes.length / 1024).toFixed(1)}KB, type: ${imageType || 'unknown'}, dimensions: ${displayWidthPixels}x${displayHeightPixels}px`);
              
              // Add image caption if available
              const caption = resource.caption || resource.description || resource.alt || resource.alt_text;
              if (caption) {
                sections.push(createParagraph(caption, {
                  bold: true,
                  size: 14,
                  spacing: { before: 200, after: 100 }
                }));
              }
              
              // Always specify type for better compatibility with docx 9.5.1
              // If we couldn't detect it, try to infer from URL or default to png
              if (!imageType) {
                // Try to infer from URL extension
                const urlLower = imageUrl.toLowerCase();
                if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) {
                  imageType = 'jpg';
                } else if (urlLower.includes('.gif')) {
                  imageType = 'gif';
                } else if (urlLower.includes('.bmp')) {
                  imageType = 'bmp';
                } else {
                  imageType = 'png'; // Default to PNG
                }
                console.warn(`Image type not detected from signature, inferred as ${imageType} from URL`);
              }
              
              // Final verification before creating ImageRun
              // Ensure we're passing a proper Uint8Array with its own buffer (not a view)
              // docx 9.5.1 requires a standalone Uint8Array, not a view into another buffer
              // CRITICAL: Since we already created a copy of the buffer above, imageBytes should be independent
              // But let's ensure we have a completely fresh Uint8Array for docx
              let finalImageData: Uint8Array;
              
              // Create a completely independent copy to ensure no buffer sharing issues
              // This is especially important when processing multiple images
              finalImageData = new Uint8Array(imageBytes.length);
              finalImageData.set(imageBytes, 0);
              
              // Verify the copy is valid
              if (finalImageData.length !== imageBytes.length) {
                throw new Error(`Failed to create Uint8Array copy: original=${imageBytes.length}, copy=${finalImageData.length}`);
              }
              
              // Verify first bytes match (to ensure data integrity)
              if (finalImageData.length > 0 && imageBytes.length > 0) {
                if (finalImageData[0] !== imageBytes[0] || finalImageData[1] !== imageBytes[1]) {
                  throw new Error('Uint8Array copy verification failed: first bytes do not match');
                }
              }
              
              // Verify the buffer is independent (different buffer reference)
              if (finalImageData.buffer === imageBytes.buffer && finalImageData.byteOffset === imageBytes.byteOffset) {
                console.warn('Warning: finalImageData shares the same buffer as imageBytes. Creating additional copy...');
                // Create yet another copy to ensure independence
                const tempCopy = new Uint8Array(imageBytes);
                finalImageData = new Uint8Array(tempCopy.length);
                finalImageData.set(tempCopy, 0);
              }
              
              // Create image paragraph with Word 2023 compatibility
              // Ensure proper dimensions and formatting for Word 2023
              // For docx 9.5.1, try passing ArrayBuffer directly instead of Uint8Array
              // Some versions of docx.js may work better with ArrayBuffer
              
              // CRITICAL: Generate a unique filename for each image to prevent deduplication
              // Even if buffers are identical, unique filenames force separate relationships
              // Use global counter to ensure absolute uniqueness across all images
              imageCounter++;
              let uniqueFilename: string;
              try {
                const urlParts = imageUrl.split('/');
                const urlFilename = urlParts[urlParts.length - 1].split('?')[0]; // Remove query params
                const filenameBase = urlFilename || 'image';
                const fileExtension = imageType === 'jpg' ? 'jpg' : imageType || 'png';
                // Use counter + timestamp + random for absolute uniqueness
                const uniqueId = `${imageCounter}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
                uniqueFilename = `${filenameBase}-${uniqueId}.${fileExtension}`;
              } catch (e) {
                // Fallback: use counter-based filename
                const fileExtension = imageType === 'jpg' ? 'jpg' : imageType || 'png';
                uniqueFilename = `image-${imageCounter}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExtension}`;
              }
              
              const imageRunOptions: any = {
                data: bufferCopy, // Using ArrayBuffer directly
                transformation: {
                  width: widthEMU, // Width in EMUs (converted from pixels)
                  height: heightEMU, // Height in EMUs (converted from pixels)
                },
                type: imageType, // Always specify type
                filename: uniqueFilename, // CRITICAL: Unique filename prevents deduplication
              };
              
              // If ArrayBuffer doesn't work, fallback to Uint8Array
              // Uncomment the line below if needed:
              // imageRunOptions.data = finalImageData;
              
              // Calculate data length and first bytes for logging
              const runDataLength = imageRunOptions.data instanceof ArrayBuffer 
                ? imageRunOptions.data.byteLength 
                : imageRunOptions.data.length;
              const runFirstBytesArray: number[] = imageRunOptions.data instanceof ArrayBuffer
                ? Array.from(new Uint8Array(imageRunOptions.data).slice(0, 4))
                : Array.from(imageRunOptions.data.slice(0, 4));
              const runFirstBytes = runFirstBytesArray.map((b) => `0x${b.toString(16).padStart(2, '0')}`).join(' ');
              
              console.log(`Creating ImageRun: filename=${uniqueFilename}, type=${imageRunOptions.type}, dataLength=${runDataLength} bytes, dimensions=${displayWidthPixels}x${displayHeightPixels}px (${widthEMU}x${heightEMU} EMU), firstBytes=${runFirstBytes}`);
              
              // Final verification: ensure all required properties are present
              if (!imageRunOptions.data || !imageRunOptions.transformation || !imageRunOptions.type) {
                throw new Error(`ImageRun options incomplete: data=${!!imageRunOptions.data}, transformation=${!!imageRunOptions.transformation}, type=${imageRunOptions.type}`);
              }
              
              if (!imageRunOptions.transformation.width || !imageRunOptions.transformation.height) {
                throw new Error(`ImageRun transformation incomplete: width=${imageRunOptions.transformation.width}, height=${imageRunOptions.transformation.height}`);
              }
              
              // Verify data is actually present and not empty
              if (!imageRunOptions.data) {
                throw new Error(`ImageRun data is missing: ${typeof imageRunOptions.data}`);
              }
              
              // Verify data length
              if (runDataLength === 0) {
                throw new Error('ImageRun data is empty');
              }
              
              console.log(`ImageRun data type: ${imageRunOptions.data instanceof ArrayBuffer ? 'ArrayBuffer' : imageRunOptions.data instanceof Uint8Array ? 'Uint8Array' : typeof imageRunOptions.data}, length: ${runDataLength}`);
              
              // Verify dimensions are reasonable pixel values
              if (widthEMU < 10 || heightEMU < 10) {
                console.warn(`Image dimensions seem very small: ${widthEMU}x${heightEMU}px`);
              }
              
              // Verify image signature matches declared type
              const checkBytes = imageRunOptions.data instanceof ArrayBuffer
                ? new Uint8Array(imageRunOptions.data)
                : imageRunOptions.data;
              
              if (imageType === 'png' && checkBytes.length >= 4) {
                if (checkBytes[0] !== 0x89 || checkBytes[1] !== 0x50 || checkBytes[2] !== 0x4E || checkBytes[3] !== 0x47) {
                  const pngBytes = Array.from(checkBytes.slice(0, 4)).map((b) => `0x${(b as number).toString(16).padStart(2, '0')}`).join(' ');
                  console.warn(`PNG signature mismatch! Expected 89 50 4E 47, got: ${pngBytes}`);
                }
              } else if (imageType === 'jpg' && checkBytes.length >= 2) {
                if (checkBytes[0] !== 0xFF || checkBytes[1] !== 0xD8) {
                  const jpgBytes = Array.from(checkBytes.slice(0, 2)).map((b) => `0x${(b as number).toString(16).padStart(2, '0')}`).join(' ');
                  console.warn(`JPEG signature mismatch! Expected FF D8, got: ${jpgBytes}`);
                }
              }
              
              const imageParagraph = new Paragraph({
                children: [
                  new ImageRun(imageRunOptions),
                ],
                alignment: (resource.alignment || resource.align || 'center') === 'left' ? 'left' :
                          (resource.alignment || resource.align || 'center') === 'right' ? 'right' : 'center',
                spacing: { 
                  after: 200,
                  before: 0,
                  line: 240, // Consistent line spacing with document defaults (matches DEFAULT_PARAGRAPH_STYLE)
                  lineRule: 'auto'
                }
              });
              
              sections.push(imageParagraph);
              
              // Add description below image
              if (description && description !== caption) {
                sections.push(createParagraph(description, {
                  size: 12,
                  spacing: { after: 200 }
                }));
              }
            } catch (error) {
              console.error(`Failed to insert image: ${imageUrl}`, error);
              // Fallback to text description
              sections.push(createParagraph(`${getResourceTypeName(resource.type)}: ${description}`, {
                spacing: { after: 100 }
              }));
            }
          } else {
            // Fallback to text description if image download failed
            sections.push(createParagraph(`${getResourceTypeName(resource.type)}: ${description}`, {
              spacing: { after: 100 }
            }));
          }
        } else if (resource.type === 'rich_text') {
          // Rich text resources - use same approach as PowerPoint export
          // Convert HTML to plain text and preserve line breaks
          const content = resource.content || resource.html || resource.text || '';
          const title = resource.title || resource.caption || '';
          
          if (title) {
            sections.push(createParagraph(title, {
              bold: true,
              size: 18,
              spacing: { before: 200, after: 100 }
            }));
          }
          
          if (content && content.trim()) {
            // Convert HTML to plain text (same as PowerPoint)
            // htmlToText converts <br>, </p>, </div>, </h1-6>, </li> to \n
            let plainText = htmlToText(content);
            
            // Sanitize text (same as PowerPoint) - remove Word metadata patterns
            plainText = sanitizeText(plainText);
            
            // Get font size from resource config if available
            const fontSize = resource.fontSize || resource.font_size;
            const fontSizeNum = fontSize && typeof fontSize === 'string' && fontSize !== 'inherit' 
              ? parseInt(fontSize.replace('px', '')) 
              : undefined;
            
            // Get text color from resource config
            let textColor = undefined;
            if (resource.textColor || resource.color) {
              textColor = convertColorToWordHex(resource.textColor || resource.color);
            }
            
            // Use createParagraphs to preserve line breaks (same approach as PowerPoint)
            // Each line from htmlToText becomes a separate paragraph
            const textParagraphs = createParagraphs(plainText, {
              size: fontSizeNum,
              color: textColor,
              spacing: { after: 200 }
            });
            
            sections.push(...textParagraphs);
          } else {
            // Fallback if no content
            sections.push(createParagraph(`${getResourceTypeName(resource.type)}: ${description}`, {
              spacing: { after: 100 }
            }));
          }
        } else {
          // Non-image, non-rich-text resources
          sections.push(createBulletItem(`${getResourceTypeName(resource.type)}: ${description}`));
        }
      }
    }
  }
  
  return sections;
}

/**
 * Creates assessment section
 */
export function createAssessmentSection(lesson: Lesson, options: WordExportOptions): Paragraph[] {
  const sections: Paragraph[] = [];
  
  if (!options.includeAssessments || !lesson.assessments) {
    return sections;
  }
  
  const assessmentIds = Object.keys(lesson.assessments);
  if (assessmentIds.length === 0) {
    return sections;
  }
  
  sections.push(createHeading('Évaluations', HeadingLevel.HEADING_1));
  
  assessmentIds.forEach((assessmentId, index) => {
    const assessment = lesson.assessments[assessmentId];
    const itemCount = assessment.items?.length || 0;
    
    // Assessment header
    sections.push(createHeading(`${index + 1}. ${sanitizeText(assessmentId)}`, HeadingLevel.HEADING_2));
    
    // Assessment metadata
    const metadataLines: string[] = [];
    if (itemCount > 0) {
      metadataLines.push(`Nombre d'items: ${itemCount}`);
    }
    if (assessment.scoring) {
      metadataLines.push(`Score maximum: ${assessment.scoring.max || 0}`);
      metadataLines.push(`Seuil de maîtrise: ${assessment.scoring.mastery_threshold || 0}`);
    }
    
    if (metadataLines.length > 0) {
      sections.push(...createParagraphs(metadataLines.join('\n'), { spacing: { after: 200 } }));
    }
    
    // Assessment items
    if (assessment.items && assessment.items.length > 0) {
      assessment.items.forEach((item, itemIndex) => {
        sections.push(createHeading(`Item ${itemIndex + 1}`, HeadingLevel.HEADING_3));
        
        const itemLines: string[] = [];
        
        // Item prompt/question
        if (item.prompt) {
          itemLines.push(`Question: ${sanitizeText(item.prompt)}`);
        }
        
        // Item choices (if multiple choice)
        if (item.choices && item.choices.length > 0) {
          const choicesText = item.choices
            .map((choice, choiceIndex) => {
              const choiceLabel = String.fromCharCode(65 + choiceIndex); // A, B, C, etc.
              const isCorrect = choice === item.answer;
              return `${choiceLabel}. ${sanitizeText(String(choice))}${isCorrect ? ' ✓ (Réponse correcte)' : ''}`;
            })
            .join('\n');
          itemLines.push(`Choix:\n${choicesText}`);
        } else if (item.answer !== undefined && item.answer !== null) {
          itemLines.push(`Réponse correcte: ${sanitizeText(String(item.answer))}`);
        }
        
        // Item points
        if (item.points !== undefined) {
          itemLines.push(`Points: ${item.points}`);
        }
        
        // Resource content if referenced
        if (item.resource_id && lesson.resources && lesson.resources[item.resource_id]) {
          const resource = lesson.resources[item.resource_id];
          itemLines.push(`\nRessource associée: ${item.resource_id} (Type: ${resource.type})`);
          
          // If it's a quiz resource, include its questions
          if (resource.type === 'quiz' && resource.questions && Array.isArray(resource.questions)) {
            itemLines.push(`\nQuestions de la ressource quiz:`);
            resource.questions.forEach((question: any, qIndex: number) => {
              const qLines: string[] = [];
              qLines.push(`  Question ${qIndex + 1}: ${sanitizeText(question.prompt || '')}`);
              
              if (question.choices && question.choices.length > 0) {
                const qChoicesText = question.choices
                  .map((choice: any, cIndex: number) => {
                    const choiceLabel = String.fromCharCode(65 + cIndex);
                    const isCorrect = choice === question.answer;
                    return `    ${choiceLabel}. ${sanitizeText(String(choice))}${isCorrect ? ' ✓' : ''}`;
                  })
                  .join('\n');
                qLines.push(`  Choix:\n${qChoicesText}`);
              } else if (question.answer !== undefined) {
                qLines.push(`  Réponse: ${sanitizeText(String(question.answer))}`);
              }
              
              itemLines.push(qLines.join('\n'));
            });
          }
          
          // If it's a rich_text resource, include its content
          if (resource.type === 'rich_text' && resource.content) {
            const richTextContent = htmlToText(resource.content);
            const sanitizedContent = sanitizeText(richTextContent);
            if (sanitizedContent.trim()) {
              itemLines.push(`\nContenu de la ressource texte:`);
              itemLines.push(sanitizedContent);
            }
          }
        }
        
        if (itemLines.length > 0) {
          sections.push(...createParagraphs(itemLines.join('\n'), { spacing: { after: 200 } }));
        }
      });
    }
  });
  
  return sections;
}

/**
 * Creates misconceptions and differentiation section
 */
export function createMisconceptionsSection(lesson: Lesson, options: WordExportOptions): Paragraph[] {
  const sections: Paragraph[] = [];
  
  if (options.detailLevel === 'summary') {
    return sections;
  }
  
  const hasMisconceptions = lesson.misconceptions_and_responses && lesson.misconceptions_and_responses.length > 0;
  const hasDifferentiation = lesson.differentiation && (
    (lesson.differentiation.supports && Object.keys(lesson.differentiation.supports).length > 0) ||
    (lesson.differentiation.extensions && Object.keys(lesson.differentiation.extensions).length > 0)
  );
  
  if (!hasMisconceptions && !hasDifferentiation) {
    return sections;
  }
  
  sections.push(createHeading('Idées Fausses et Différenciation', HeadingLevel.HEADING_1));
  
  // Misconceptions
  if (hasMisconceptions) {
    sections.push(createParagraph('Idées Fausses:', { bold: true, spacing: { after: 100 } }));
    lesson.misconceptions_and_responses.forEach((mr, index) => {
      if (mr.misconception) {
        // Use createParagraphs to preserve line breaks
        sections.push(...createParagraphs(`${index + 1}. ${sanitizeText(mr.misconception)}`));
      }
      if (mr.response) {
        // Use createParagraphs to preserve line breaks
        sections.push(...createParagraphs(`   Réponse: ${sanitizeText(mr.response)}`, { spacing: { after: 200 } }));
      }
    });
  }
  
  // Differentiation
  if (hasDifferentiation) {
    if (lesson.differentiation.supports && Object.keys(lesson.differentiation.supports).length > 0) {
      sections.push(createParagraph('Supports:', { bold: true, spacing: { before: 200, after: 100 } }));
      Object.values(lesson.differentiation.supports).forEach((support, index) => {
        const sanitized = sanitizeText(String(support));
        if (sanitized) {
          sections.push(createBulletItem(sanitized));
        }
      });
    }
    
    if (lesson.differentiation.extensions && Object.keys(lesson.differentiation.extensions).length > 0) {
      sections.push(createParagraph('Extensions:', { bold: true, spacing: { before: 200, after: 100 } }));
      Object.values(lesson.differentiation.extensions).forEach((extension, index) => {
        const sanitized = sanitizeText(String(extension));
        if (sanitized) {
          sections.push(createBulletItem(sanitized));
        }
      });
    }
  }
  
  return sections;
}

/**
 * Creates notes section
 */
export function createNotesSection(lesson: Lesson, options: WordExportOptions): Paragraph[] {
  const sections: Paragraph[] = [];
  
  if (!options.includeNotes) {
    return sections;
  }
  
  const hasClassroomNotes = lesson.classroom_management_notes && lesson.classroom_management_notes.length > 0;
  const hasOfflineVariant = lesson.offline_variant && lesson.offline_variant.directions && lesson.offline_variant.directions.length > 0;
  const hasHomeConnection = lesson.home_connection && lesson.home_connection.family_note;
  
  if (!hasClassroomNotes && !hasOfflineVariant && !hasHomeConnection) {
    return sections;
  }
  
  sections.push(createHeading('Notes Supplémentaires', HeadingLevel.HEADING_1));
  
  // Classroom management notes
  if (hasClassroomNotes) {
    sections.push(createParagraph('Notes de Gestion de Classe:', { bold: true, spacing: { after: 100 } }));
    lesson.classroom_management_notes.forEach((note, index) => {
      const sanitized = sanitizeText(note);
      if (sanitized) {
        sections.push(createBulletItem(sanitized));
      }
    });
  }
  
  // Offline variant
  if (hasOfflineVariant) {
    sections.push(createParagraph('Variante Hors Ligne:', { bold: true, spacing: { before: 200, after: 100 } }));
    lesson.offline_variant.directions.forEach((direction, index) => {
      const sanitized = sanitizeText(direction);
      if (sanitized) {
        sections.push(createBulletItem(sanitized));
      }
    });
  }
  
  // Home connection
  if (hasHomeConnection) {
    sections.push(createParagraph('Connexion avec la Maison:', { bold: true, spacing: { before: 200, after: 100 } }));
    const sanitized = sanitizeText(lesson.home_connection.family_note);
    if (sanitized) {
      // Use createParagraphs to preserve line breaks
      sections.push(...createParagraphs(sanitized));
    }
  }
  
  return sections;
}
